import { CHAIN_ID } from '@derivation-tech/context';
import { JsonRpcProvider, JsonRpcSigner } from '@ethersproject/providers';
import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import { BigNumber, ContractReceipt } from 'ethers';

import { GlobalModalType, OPERATION_TX_TYPE } from '@/constants';
import { WrappedBigNumber } from '@/entities/WrappedBigNumber';
import { WrappedPortfolio } from '@/entities/WrappedPortfolio';
import { WrappedQuote } from '@/entities/WrappedQuote';
import { TokenInfo } from '@/types/token';
import { parseSendingTxMessageMapping } from '@/utils/notification';
// import { toWad } from '@/utils/numberUtil';

import { QUERY_KEYS } from '@/constants/query';
import SentryService from '@/entities/SentryService';
import { queryClient } from '@/global';
import { IAssetsBalance } from '@/types/assets';
import { Context } from '@derivation-tech/context';
import { getAllAccountsFromChain } from '../account/actions';
import { fetchGateBalanceAction } from '../balance/actions';
import { setOpenModal } from '../global/actions';
import { sendTransaction } from '../transaction/actions';
import { checkTokenAllowance } from '../user/actions';

export const setPortfolioAssetSelect = createAction<{ assetSelected: IAssetsBalance | null }>(
  'portfolio/setPortfolioAssetSelect',
);
export const clickOpenSettledPairModal = createAsyncThunk(
  'portfolio/clickOpenSettledPairModal',
  async (
    {
      portfolio,
    }: {
      chainId: CHAIN_ID;
      userAddr: string;
      portfolio: WrappedPortfolio;
    },
    { dispatch },
  ): Promise<WrappedPortfolio> => {
    dispatch(setOpenModal({ type: GlobalModalType.SETTLE }));
    return portfolio;
  },
);

export const settle = createAsyncThunk(
  'portfolio/settle',
  async (
    {
      chainId,
      portfolio,
      userAddr,
      signer,
      sdkContext,
      provider,
    }: {
      chainId: CHAIN_ID;
      userAddr: string;
      portfolio: WrappedPortfolio;
      sdkContext: Context;
      signer: JsonRpcSigner;
      provider: JsonRpcProvider;
    },
    { dispatch },
  ): Promise<ContractReceipt | undefined> => {
    try {
      const pair = portfolio.rootPair;
      const result = await dispatch(
        sendTransaction({
          signer,
          sendFunc: async () => {
            console.record(
              'tx',
              `Try to send [${OPERATION_TX_TYPE.SETTLE}] operation tx for pair [${pair.symbol}]`,
              undefined,
              {
                signer,
                expiry: pair.expiry,
                pair: pair,
                instrument: pair.rootInstrument,
              },
            );
            // return await provider.getTransaction(`0xf3fe7c3513f12b731de791b2adc1cc24841a26a512d354612370e818dd742aa7`);
            const populatedTx = await sdkContext.perp.instrument.settle(
              {
                instrumentAddr: pair.rootInstrument.instrumentAddr,
                expiry: pair.expiry,
                target: userAddr,
              },
              { from: userAddr },
            );
            return signer.sendTransaction(populatedTx);
          },

          chainId,
          userAddr,
          txParams: {
            type: OPERATION_TX_TYPE.SETTLE,
            instrument: {
              baseSymbol: pair.rootInstrument.baseToken.symbol,
              quoteSymbol: pair.rootInstrument.quoteToken.symbol,
              isInverse: pair.rootInstrument.isInverse,
              quoteDecimal: pair.rootInstrument.quoteToken.decimals,
            },
          },
          wssProvider: provider,
        }),
      ).unwrap();
      if (result?.status) {
        queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.PORTFOLIO.PORTFOLIO(chainId, userAddr, pair.rootInstrument.instrumentAddr, pair.expiry),
        });
        dispatch(
          checkTokenAllowance({
            userAddress: userAddr,
            chainId,
            marginToken: pair.rootInstrument.quoteToken,
            sdk: sdkContext,
          }),
        );

        queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.BALANCE.TOKEN_BALANCE(chainId, userAddr),
        });
        dispatch(
          fetchGateBalanceAction({
            chainId,
            userAddr,
            tokens: [pair.rootInstrument.quoteToken],
            block: result.blockNumber,
          }),
        );
      }

      return result;
    } catch (error) {
      SentryService.captureException(error, {
        name: 'portfolio/settle',
        chainId,
        userAddr,
      });
      console.log('🚀 ~ portfolio settle error:', error);
      throw error;
    }
  },
);

export const setPortfolioDepositOrWithdrawOperateToken = createAction<{
  chainId: number;
  userAddr: string;
  tokenInfo: TokenInfo;
}>('portfolio/setPortfolioDepositOrWithdrawOperateToken');

export const claimWithdraw = createAsyncThunk(
  'portfolio/claimWithdraw',
  async (
    {
      chainId,
      userAddr,
      signer,
      sdkContext,
      provider,
      quote,
      amount,
    }: {
      chainId: CHAIN_ID;
      userAddr: string;
      sdkContext: Context;
      signer: JsonRpcSigner;
      provider: JsonRpcProvider;
      quote: WrappedQuote;
      amount: BigNumber;
    },
    { dispatch },
  ): Promise<ContractReceipt | undefined> => {
    try {
      const txType = OPERATION_TX_TYPE.CLAIM_WITHDRAW;
      const receipt = await dispatch(
        sendTransaction({
          signer,
          sendFunc: async () => {
            console.record('tx', `Try to send [${txType}] operation tx for quote [${quote.symbol}]`, undefined, {
              chainId,
              userAddr,
              signer,
              quote,
              amount: amount,
              quoteSymbol: quote.symbol,
              to: userAddr,
            });
            const populatedTx = await sdkContext.perp.gate.claimPendingWithdraw(quote.address, userAddr, {
              from: userAddr,
            });
            return signer.sendTransaction(populatedTx);
          },
          chainId,
          userAddr,
          txParams: {
            isDisableNotification: true,
            type: txType,
            instrument: {
              quoteSymbol: quote.symbol,
              baseSymbol: '',
              isInverse: false,
              quoteDecimal: quote.decimals,
            },
            sendingTemplate: parseSendingTxMessageMapping[txType]!(WrappedBigNumber.from(amount), quote.symbol),
          },
          wssProvider: provider,
        }),
      ).unwrap();
      console.log('🚀 ~ sdk release result:', receipt);
      if (receipt?.status === 1) {
        const block = receipt.blockNumber;
        dispatch(
          getAllAccountsFromChain({
            chainId,
            userAddr,
            blockNumber: block,
            sdkContext,
            instruments: [],
            needFetchInstruments: true,
          }),
        );
        queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.BALANCE.TOKEN_BALANCE(chainId, userAddr),
        });
        dispatch(
          fetchGateBalanceAction({
            chainId,
            userAddr,
            tokens: [quote],
            block: block,
          }),
        );
      }
      return receipt;
    } catch (e) {
      console.error('portfolio/claimWithdraw error', e);
      throw e;
    }
  },
);
